/**
 * Einen Platzsatz aufteilen, in dem zwei Orte zusammengefallen sind.
 *
 * Die Pipeline ordnet Tauchgänge ihrem Platz über den **exakten Namen** zu.
 * Richtig für die 314 Überlappungen zwischen Garmin und Diving Log, falsch
 * sobald ein Name zweimal vorkommt: „Madivaru Kandu" gibt es in mehreren
 * Atollen, „Shark Point" auf der halben Welt. Beide Orte hängen danach an einer
 * Position — der, die zufällig zuerst da war.
 *
 * `make kollisionen` findet solche Sätze. Hier werden sie getrennt.
 *
 * **Das Gegenstück zum Zusammenführen, und harmloser als dieses.** Es
 * verschwindet kein Stammsatz: Ein zweiter entsteht, ein Teil der Tauchgänge
 * hängt um. Wer sich vertut, führt die beiden wieder zusammen.
 *
 * **Die Gruppen werden gemessen, nicht geraten.** Tauchgänge tragen den
 * GPS-Punkt ihres Einstiegs; Punkte, die beieinanderliegen, sind derselbe Ort.
 * Dieselbe Rechnung und dieselbe Schwelle wie in
 * `src/tauchlogbuch/kollisionen.py`. Tauchgänge ohne GPS lassen sich so nicht
 * einordnen — sie kommen in eine eigene Gruppe und werden von Hand zugewiesen.
 */

import { SPEICHER, alleTauchgaenge } from './db.js';
import { entfernung } from './geo.js';
import { neueId, speichere } from './saetze.js';

/** Ein Tauchplatz ist keine Region — wie in der Pipeline. */
export const GPS_GRENZE_KM = 20;

const hatPunkt = (tg) => typeof tg?.entry_lat === 'number' && typeof tg?.entry_lon === 'number'
  && !(tg.entry_lat === 0 && tg.entry_lon === 0);

/**
 * Tauchgänge zu Orten zusammenfassen — gierig, das reicht hier.
 *
 * Ein Platz hat zwei, vielleicht fünf tatsächliche Orte; ein ausgefeiltes
 * Verfahren braucht es dafür nicht. Jeder Punkt kommt zur ersten Gruppe in
 * Reichweite, sonst macht er eine neue auf. Sortiert wird vorher nach Zeit,
 * damit dieselbe Eingabe immer dieselben Gruppen ergibt.
 */
export function gruppiere(tauchgaenge, grenze = GPS_GRENZE_KM) {
  // Nach Zeit, bei Gleichstand nach Kennung: Ohne den zweiten Schlüssel hinge
  // die Gruppenbildung bei gleichen Zeitstempeln an der Reihenfolge der
  // Eingabe, und dieselben Daten ergäben zweimal etwas anderes.
  const mitPunkt = tauchgaenge.filter(hatPunkt).sort((a, b) =>
    String(a.start_utc ?? '').localeCompare(String(b.start_utc ?? ''))
    || String(a.id ?? '').localeCompare(String(b.id ?? '')));
  const ohnePunkt = tauchgaenge.filter((tg) => !hatPunkt(tg));

  const gruppen = [];
  for (const tg of mitPunkt) {
    const punkt = { lat: tg.entry_lat, lon: tg.entry_lon };
    const passend = gruppen.find((g) => entfernung(g, punkt) <= grenze);
    if (passend) {
      passend.tauchgaenge.push(tg);
      continue;
    }
    gruppen.push({ lat: punkt.lat, lon: punkt.lon, tauchgaenge: [tg], ohneGps: false });
  }
  // Der Ort einer Gruppe ist der Mittelwert ihrer Punkte, nicht der erste:
  // Sonst hinge die Position davon ab, welcher Tauchgang zuerst kam.
  for (const gruppe of gruppen) {
    gruppe.lat = mittel(gruppe.tauchgaenge.map((tg) => tg.entry_lat));
    gruppe.lon = mittel(gruppe.tauchgaenge.map((tg) => tg.entry_lon));
  }
  gruppen.sort((a, b) => b.tauchgaenge.length - a.tauchgaenge.length);

  if (ohnePunkt.length > 0) {
    gruppen.push({ lat: null, lon: null, tauchgaenge: ohnePunkt, ohneGps: true });
  }
  return gruppen;
}

const mittel = (werte) => werte.reduce((summe, w) => summe + w, 0) / werte.length;

/** Fünf Nachkommastellen sind gut einen Meter — wie beim Verorten. */
const rund = (wert) => (typeof wert === 'number' ? Math.round(wert * 1e5) / 1e5 : null);

/**
 * Was das Aufteilen bewirkt — gerechnet, bevor irgendetwas geschrieben wird.
 *
 * Die heikle Stelle ist die Position des **bleibenden** Satzes. Bei „Shark
 * Point" steht dort Galápagos, während die zwei bleibenden Tauchgänge von 2005
 * auf den Philippinen stattfanden: Die Position gehört zum wegziehenden Ort und
 * wäre danach schlicht falsch.
 *
 * **Entschieden wird das nicht hier.** Ein erster Entwurf las die Herkunft
 * (`geocode_source = 'dive_gps'`) und leerte danach automatisch — an den echten
 * Daten stand dort `null`, und die Regel lief ins Leere. Woher eine Koordinate
 * stammt, ist im Bestand nicht verlässlich vermerkt; darauf eine stille
 * Löschung zu stützen, wäre geraten. `kannLeeren` sagt deshalb nur, dass es
 * sich lohnt zu fragen: Alles mit GPS zieht weg, der Rest hat keins, und eine
 * Position ist da. Gefragt wird im Dialog.
 */
export function plan(platz, alle, umziehendeIds) {
  const zieht = new Set(umziehendeIds);
  const umziehend = alle.filter((tg) => zieht.has(tg.id));
  const bleibend = alle.filter((tg) => !zieht.has(tg.id));

  return {
    umziehend,
    bleibend,
    neuerOrt: ortAus(umziehend),
    kannLeeren: umziehend.some(hatPunkt) && !bleibend.some(hatPunkt)
      && typeof platz?.lat === 'number',
    machbar: umziehend.length > 0 && bleibend.length > 0,
  };
}

function ortAus(tauchgaenge) {
  const mitPunkt = tauchgaenge.filter(hatPunkt);
  if (mitPunkt.length === 0) return null;
  return {
    lat: rund(mittel(mitPunkt.map((tg) => tg.entry_lat))),
    lon: rund(mittel(mitPunkt.map((tg) => tg.entry_lon))),
  };
}

/**
 * Aufteilen in drei Schritten: neuen Satz anlegen, Tauchgänge umhängen, den
 * alten anpassen.
 *
 * Die Reihenfolge ist nicht beliebig — dieselbe Überlegung wie beim
 * Zusammenführen. Bräche es nach Schritt zwei ab, stünden zwei Sätze da, deren
 * Tauchgänge richtig verteilt sind und von denen einer eine Position zu viel
 * hat; harmlos und beim zweiten Anlauf zu Ende zu bringen. Umgekehrt hätte man
 * Tauchgänge, die auf einen Platz zeigen, den es noch nicht gibt.
 */
export async function teileAuf(db, platz, umziehendeIds, neuerName, optionen = {}) {
  const name = String(neuerName ?? '').trim();
  if (!name) throw new Error('Der neue Platz braucht einen Namen.');
  if (name === platz.name) throw new Error('Der neue Platz braucht einen anderen Namen.');

  const alle = (await alleTauchgaenge(db)).filter((tg) => tg.site_id === platz.id);
  const { umziehend, bleibend, neuerOrt, machbar } = plan(platz, alle, umziehendeIds);
  const leeren = Boolean(optionen.leeren);
  if (!machbar) {
    throw new Error('Aufteilen braucht Tauchgänge auf beiden Seiten.');
  }

  const neu = {
    ...platz,
    id: neueId(),
    name,
    lat: neuerOrt ? neuerOrt.lat : null,
    lon: neuerOrt ? neuerOrt.lon : null,
    // Die Position des neuen Satzes kommt aus dem GPS der umgezogenen
    // Tauchgänge — dieselbe Herkunft, die die Pipeline dafür vergibt.
    geocode_source: neuerOrt ? 'dive_gps' : null,
  };
  await speichere(db, SPEICHER.sites, neu, optionen);

  await speichere(db, SPEICHER.dives,
    umziehend.map((tg) => ({ ...tg, site_id: neu.id, site_name: neu.name })), optionen);

  // Auch die Region gehört zum wegziehenden Ort: Bei „Shark Point" steht dort
  // „Galápagos", während die bleibenden Tauchgänge philippinisch sind.
  if (leeren) {
    await speichere(db, SPEICHER.sites,
      { ...platz, lat: null, lon: null, geocode_source: null, region: null }, optionen);
  }
  return {
    neu, umgehaengt: umziehend.length, geblieben: bleibend.length, geleert: leeren,
  };
}
